import { useCallback, useMemo } from 'react';
import { useClientProfile, useLanguage } from 'store';
import IntlFormatter from './IntlFormatter';
import CRYPTO_CURRENCY_CODE from './cryptoList.ts';

type DateValue = Date | string | number;

const MAX_LIMIT_TO_FORMAT_WITH_DECIMAL = 10000;

const useIntl = (): {
  formatMoney: (value: number) => string;
  formatDynamicFractionMoney: (value: number) => string;
  formatNumber: (value: number) => string;
  formatDate: (date: DateValue) => string;
} => {
  const [language] = useLanguage();
  const [clientProfile] = useClientProfile();

  const currency = clientProfile.data?.client.currency || undefined;
  const style = currency ? 'currency' : 'decimal';
  const intlFormatterInstance = IntlFormatter.getInstance();

  const intlCurrencyFormat = useMemo(
    () =>
      intlFormatterInstance.getNumberFormatter({
        languageCode: language.current.alpha2Code,
        currency,
        style,
        fractionDigits: 2,
      }),
    [
      language.current.alpha2Code,
      currency,
      style,
      intlFormatterInstance.getNumberFormatter,
    ],
  );

  const intlCurrencyFormatNoDecimals = useMemo(
    () =>
      intlFormatterInstance.getNumberFormatter({
        languageCode: language.current.alpha2Code,
        currency,
        style,
        fractionDigits: 0,
      }),
    [
      language.current.alpha2Code,
      currency,
      style,
      intlFormatterInstance.getNumberFormatter,
    ],
  );

  const intlCurrencyFormatDynamicFraction = useMemo(
    () =>
      intlFormatterInstance.getNumberFormatter({
        languageCode: language.current.alpha2Code,
        currency,
        style,
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }),
    [
      language.current.alpha2Code,
      currency,
      style,
      intlFormatterInstance.getNumberFormatter,
    ],
  );

  const intlDateFormat = useMemo(
    () => intlFormatterInstance.getDateFormatter(language.current.alpha2Code),
    [language.current.alpha2Code, intlFormatterInstance.getDateFormatter],
  );

  const formatMoney = useCallback(
    (value: number): string => {
      const decimalValue = value / 100;

      if (currency && CRYPTO_CURRENCY_CODE.has(currency)) {
        return `${intlCurrencyFormat.format(decimalValue)} ${currency}`;
      }

      if (decimalValue >= MAX_LIMIT_TO_FORMAT_WITH_DECIMAL) {
        return `${intlCurrencyFormatNoDecimals.format(decimalValue)}`;
      }

      return `${intlCurrencyFormat.format(decimalValue)}`;
    },
    [currency, intlCurrencyFormat.format, intlCurrencyFormatNoDecimals.format],
  );

  const formatNumber = useCallback((value: number): string => {
    // this func will be change later
    return value.toString();
  }, []);

  const formatDynamicFractionMoney = useCallback(
    (value: number): string =>
      intlCurrencyFormatDynamicFraction.format(value).toString(),
    [intlCurrencyFormatDynamicFraction.format],
  );

  const formatDate = useCallback(
    (date: DateValue): string => {
      return `${intlDateFormat.format(new Date(date))}`;
    },
    [intlDateFormat.format],
  );

  return {
    formatMoney,
    formatNumber,
    formatDate,
    formatDynamicFractionMoney,
  };
};

export default useIntl;
